import _ from "lodash";
import {
  emptyPage,
  fromEntries,
  isJsonString,
  remove_prefix,
} from "../helpers";
import { getRandomId } from "@craftjs/utils";

function getAllChildNodes(nodes, id) {
  if (!nodes[id] || nodes[id]?.nodes?.length === 0) {
    return [];
  }
  return [
    ...nodes[id].nodes,
    ..._.flatten(
      nodes[id].nodes.map((id) => {
        return getAllChildNodes(nodes, id);
      }),
    ),
  ];
}

// to save as a component
const handleSaveComponent = (id, query) => {
  const tree = query.node(id).toNodeTree();
  const nodePairs = Object.keys(tree.nodes).map((id) => [
    id,
    query.node(id).toSerializedNode(),
  ]);
  const serializedNodesJSON = JSON.stringify(fromEntries(nodePairs));
  const saveData = {
    rootNodeId: tree.rootNodeId,
    nodes: serializedNodesJSON,
  };
  // nodetree to json
  return JSON.stringify(saveData);
};

// to save as a component
const updateComponentOnPageChange = (
  query,
  projectComponents,
  setProjectComponents,
) => {
  Object.keys(projectComponents).map((componentID) => {
    if (projectComponents[componentID].root_node) {
      const tree = query
        .node(projectComponents[componentID].root_node)
        .toNodeTree();
      const nodePairs = Object.keys(tree.nodes).map((id) => [
        id,
        query.node(id).toSerializedNode(),
      ]);
      const serializedNodesJSON = JSON.stringify(fromEntries(nodePairs));
      const saveData = {
        rootNodeId: tree.rootNodeId,
        nodes: serializedNodesJSON,
      };
      // nodetree to json
      setProjectComponents({
        ...projectComponents,
        [componentID]: {
          ...projectComponents[componentID],
          content: JSON.stringify(saveData),
        },
      });
    }
  });
  return undefined;
};

// to save as a component
const createComponentPage = (component) => {
  var content = component.content;
  if (content && isJsonString(content)) {
    var page = JSON.parse(emptyPage());
    var componentJson = JSON.parse(content);
    if (!_.isEmpty(componentJson.nodes)) {
      var componentJsonNodes = JSON.parse(componentJson.nodes);
      if (!_.isEmpty(componentJsonNodes)) {
        // Update nodes
        Object.keys(componentJsonNodes).map((node) => {
          page[node] = { ...componentJsonNodes[node] };
        });

        // Update ROOT Node
        page["ROOT"].nodes = [componentJson.rootNodeId];

        // Update ROOT Child Node's parent
        page[componentJson.rootNodeId].parent = "ROOT";
        page[componentJson.rootNodeId].props.isComponent = undefined;
        page[componentJson.rootNodeId].props.componentID = undefined;
        return JSON.stringify(page);
      }
    }
  }

  return "";
};

// to save as a component
const updatePageWithComponents = (tmpPage, projectComponents, query) => {
  // Before deserialize, update the page's components with current updated components
  if (isJsonString(tmpPage)) {
    var tmpPageJson = JSON.parse(tmpPage);
    var tmpPageUpdated = JSON.parse(tmpPage);

    // Loop through all the nodes and check if any of the container is component with specific ID
    // Update all component nodes.
    Object.keys(tmpPageJson).map((pageNode) => {
      if (
        tmpPageJson[pageNode]?.custom?.slug === "grigora/container" &&
        tmpPageJson[pageNode]?.props?.isComponent &&
        tmpPageJson[pageNode]?.props?.componentID
      ) {
        let componentID =
          "component-" + tmpPageJson[pageNode].props.componentID;
        if (
          projectComponents[componentID]?.content &&
          isJsonString(projectComponents[componentID].content)
        ) {
          var componentJson = JSON.parse(
            projectComponents[componentID].content,
          );
          if (!_.isEmpty(componentJson)) {
            var newNodes = JSON.parse(componentJson.nodes);

            if (!_.isEmpty(newNodes)) {
              const nodePairs = Object.keys(newNodes).map((id) => {
                let nodeId = id;

                return [
                  nodeId,
                  query
                    .parseSerializedNode(newNodes[id])
                    .toNode((node) => (node.id = nodeId)),
                ];
              });
              var tree = {
                rootNodeId: componentJson.rootNodeId,
                nodes: fromEntries(nodePairs),
              };
              var newComponentTree = getCloneTree(tree, query);
              if (newComponentTree.nodes) {
                // Replace with new component tree
                Object.keys(newComponentTree.nodes).map((newNodeID) => {
                  tmpPageUpdated[newNodeID] = {
                    ...newComponentTree.nodes[newNodeID].data,
                    type: {
                      resolvedName: newComponentTree.nodes[newNodeID].data.name,
                    },
                  };
                });

                // Update the old rootID with new rootID
                var parent = tmpPageUpdated[pageNode].parent;
                var duplicateNodes = getAllChildNodes(tmpPageUpdated, pageNode);
                delete tmpPageUpdated[pageNode];
                duplicateNodes.map((node) => {
                  delete tmpPageUpdated[duplicateNodes];
                });

                if (tmpPageUpdated[parent]) {
                  var index = tmpPageUpdated[parent]?.nodes?.indexOf(pageNode);

                  if (index !== -1) {
                    tmpPageUpdated[parent].nodes[index] =
                      newComponentTree.rootNodeId;
                  }
                }

                tmpPageUpdated[newComponentTree.rootNodeId].parent = parent;
                tmpPageUpdated[newComponentTree.rootNodeId].props[
                  "isComponent"
                ] = true;
                tmpPageUpdated[newComponentTree.rootNodeId].props[
                  "componentID"
                ] = remove_prefix(componentID, "component-");
              }
            }
          }
        }
      }
    });

    tmpPage = JSON.stringify(tmpPageUpdated);
  }

  return tmpPage;
};

// Create a clone node tree
const getCloneTree = (tree, query) => {
  const newNodes = {};
  const changeNodeId = (node, newParentId) => {
    const newNodeId = getRandomId();
    const childNodes = node.data.nodes.map((childId) =>
      changeNodeId(tree.nodes[childId], newNodeId),
    );
    const linkedNodes = Object.keys(node.data.linkedNodes).reduce((acc, id) => {
      const newLinkedNodeId = changeNodeId(
        tree.nodes[node.data.linkedNodes[id]],
        newNodeId,
      );
      return {
        ...acc,
        [id]: newLinkedNodeId,
      };
    }, {});

    let tmpNode = {
      ...node,
      id: newNodeId,
      data: {
        ...node.data,
        parent: newParentId || node.data.parent,
        nodes: childNodes,
        linkedNodes,
      },
    };

    let freshNode = query.parseFreshNode(tmpNode).toNode();
    newNodes[newNodeId] = freshNode;
    return newNodeId;
  };

  const rootNodeId = changeNodeId(tree.nodes[tree.rootNodeId]);
  return {
    rootNodeId,
    nodes: newNodes,
  };
};

const addNodeTree = (oldTree, actions, query, node, index) => {
  // get the template from your database
  const data = JSON.parse(oldTree);
  const newNodes = JSON.parse(data.nodes);
  const nodePairs = Object.keys(newNodes).map((id) => {
    let nodeId = id;

    return [
      nodeId,
      query
        .parseSerializedNode(newNodes[id])
        .toNode((node) => (node.id = nodeId)),
    ];
  });
  const tree = { rootNodeId: data.rootNodeId, nodes: fromEntries(nodePairs) };
  const newTree = getCloneTree(tree, query);

  // add templates where you want
  actions.addNodeTree(newTree, node, index);
  if (actions.selectNode) {
    actions.selectNode(newTree.rootNodeId);
  }
  return newTree.rootNodeId;
};

export {
  handleSaveComponent,
  updateComponentOnPageChange,
  updatePageWithComponents,
  getCloneTree,
  addNodeTree,
  createComponentPage,
  getAllChildNodes,
};
